import type { ReactNode } from "react";

const HOME = "https://ottercraft.dev";

/**
 * The static, crawlable half of the page: what OtterQR does, how a QR code
 * actually works, and the questions that come up. Pure markup with no runtime
 * state, so the same component is prerendered into index.html at build time
 * (see prerender.tsx) and rendered again by the app on mount.
 *
 * Deliberately short — it sits under a tool, not on a landing page. The
 * long-form guides live on ottercraft.dev; this only needs to say what the
 * thing is. Questions are collapsed <details> rows: indexable, one line each.
 */
export function Explainer() {
  return (
    <section id="about" aria-labelledby="about-title" className="mt-16 border-t border-rule pt-12">
      <p className="eyebrow">About OtterQR</p>
      <h2
        id="about-title"
        className="mt-2 max-w-[600px] font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-pine sm:text-[30px]"
      >
        A QR code generator that keeps the code yours.
      </h2>
      <p className="mt-3 max-w-[600px] text-[14.5px] leading-[1.6] text-soft">
        Point it at a link, a Wi-Fi network, a phone number or a contact card. Choose how the
        modules are drawn, colour it, drop your logo in the middle, and save it as a PNG for the
        web or an SVG for the printer. Most QR sites generate the code on their server and keep a
        redirect in the middle of it, so the code stops working the day they do. This one is drawn
        in your browser and points straight at whatever you typed.
      </p>

      <ol className="mt-8 grid gap-6 sm:grid-cols-3">
        <Step n="01" title="You type what it should hold">
          A link, some text, Wi-Fi details, an email, an SMS, a number, a contact card. The form
          writes the payload in the shape phone cameras already understand.
        </Step>
        <Step n="02" title="Your browser encodes it">
          Your browser packs the text, adds the error-correction and masks the module grid. That is
          the real QR algorithm, running on this page, and it asks a server for nothing.
        </Step>
        <Step n="03" title="You style it and save it">
          The shapes, the colours and the logo become one SVG. PNG, JPG and WebP are that same SVG
          painted onto a canvas at the size you pick.
        </Step>
      </ol>

      <p className="mt-8 max-w-[680px] text-[13.5px] leading-[1.6] text-soft">
        The codes are static, which is the point: no account, no tracking redirect, no expiry, and
        nothing that can be switched off later. They are yours to print, and they will still work
        in ten years. After the first visit the tool also works offline.
      </p>

      <h3 id="faq" className="mt-12 scroll-mt-6 font-serif text-[20px] font-semibold leading-tight text-pine">
        Questions people ask
      </h3>
      <div className="mt-3 divide-y divide-rule border-y border-rule">
        <Faq q="Do these QR codes expire, or can they be edited later?">
          Neither. The code <em>is</em> the link — the address is encoded in the pattern itself,
          not stored on a server that could redirect it somewhere else. That means it never
          expires and nobody can count your scans, but it also means changing the destination
          means making a new code.
        </Faq>
        <Faq q="Can I put my logo in the middle?">
          Yes, and it is optional. Drop a picture into the Logo card. The modules underneath are
          left undrawn rather than covered, and error correction moves to High so the missing part
          can be reconstructed. Keep it under about a fifth of the width and scan the result once
          before printing a thousand of them.
        </Faq>
        <Faq q="Which format should I download?">
          <Code>SVG</Code> for anything printed — it is vector, so it is sharp at any size, and a
          designer can recolour it. <Code>PNG</Code> for screens and documents; it keeps a
          transparent background if you asked for one. <Code>JPG</Code> only when something insists
          on it, since it has no transparency and its compression softens the edges. <Code>WebP</Code>{" "}
          when file size matters more than reach.
        </Faq>
        <Faq q="Why does my code not scan?">
          Usually one of five things: too little contrast between the code and its background, a
          light code on a dark background, no quiet zone around it (four modules of empty space), a
          logo covering too much, or round corner markers — those three squares are how a scanner
          finds the code in the first place, and not every scanner accepts them as circles. The
          panel warns about each of those as you go.
        </Faq>
        <Faq q="Is the Wi-Fi password safe in a QR code?">
          The password is written into the pattern in plain text — anyone who can photograph the
          code can read it. That is exactly what makes it convenient for guests, and exactly why a
          guest network is the right one to put on the wall.
        </Faq>
        <Faq q="Is anything uploaded?">
          No. There is no server in this app: the encoder, the renderer and the image export all
          run in this browser tab, and the logo you pick is read straight off your device. You can
          turn off your connection after the page loads and it keeps working.
        </Faq>
      </div>

      <p className="mt-6 text-[13px] leading-[1.6] text-muted">
        OtterQR is part of <a className="link" href={HOME}>ottercraft.dev</a>, a few small free
        tools that run in your browser.
      </p>
    </section>
  );
}

function Step({ n, title, children }: { n: string; title: string; children: ReactNode }) {
  return (
    <li className="flex flex-col gap-1">
      <span className="font-mono text-[11px] text-teal-deep">{n}</span>
      <span className="font-serif text-[16px] font-semibold leading-tight text-pine">{title}</span>
      <p className="text-[13px] leading-[1.55] text-soft">{children}</p>
    </li>
  );
}

function Faq({ q, children }: { q: string; children: ReactNode }) {
  return (
    <details className="group py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-serif text-[16px] leading-tight text-pine [&::-webkit-details-marker]:hidden">
        {q}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="shrink-0 text-muted transition-transform group-open:rotate-45" aria-hidden="true">
          <path d="M12 5v14" /><path d="M5 12h14" />
        </svg>
      </summary>
      <p className="mt-2 max-w-[720px] text-[14px] leading-[1.6] text-soft">{children}</p>
    </details>
  );
}

function Code({ children }: { children: ReactNode }) {
  return <code className="rounded bg-pine/10 px-1 py-0.5 font-mono text-[12px] text-pine-deep">{children}</code>;
}
